import { useState, useEffect, useCallback, useRef } from 'react';
import { FormikHelpers } from 'formik';
import useBrands from 'data/hooks/useBrands';
import useWorkingHours from 'data/hooks/useWorkingHours';
import useWorkingHoursActions from 'data/hooks/useWorkingHoursActions';
import { PropsShowModal } from 'components/Modals/ModalUpdate/types';
import { useAskModal } from 'components/Modals/AskYesNo';
import { BusyModeModalProps } from 'components/Modals/BusyModeModal/types';
import { useSnackbar } from 'notistack';
import useBrandActions from 'data/hooks/useBrandActions';
import { initialValues } from './constants';

type FormValues = typeof initialValues;

const useConnect = () => {
  const { brands } = useBrands();
  const { unpause } = useBrandActions();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [applyToAll, setApplyToAll] = useState<boolean>(false);
  const [busyModeChecked, setBusyModeChecked] = useState<boolean>(false);
  const [showBusyModeModal, setShowBusyModeModal] =
    useState<BusyModeModalProps>({
      brandId: selectedBrandId,
      open: false,
    });
  const [showModal, setShowModal] = useState<PropsShowModal>({
    open: false,
    selectedDay: undefined,
    title: '',
  });

  const formRef = useRef();

  const { updateWorkingHours } = useWorkingHoursActions();

  const { isLoading, workingHours, refetch } = useWorkingHours(selectedBrandId);

  useEffect(() => {
    if (selectedBrandId !== '') {
      refetch();
    }
  }, [refetch, selectedBrandId]);

  const handleBusyModeOpen = useCallback(
    async (brandId) => {
      try {
        await unpause({ brandId });
        setBusyModeChecked(false);
      } catch (error) {
        enqueueSnackbar(`Status change failed`, {
          variant: 'error',
        });
      }
    },
    [unpause, enqueueSnackbar, setBusyModeChecked],
  );

  const handleSubmitModalUpdate = useCallback(
    async (values: FormValues, { setErrors }: FormikHelpers<FormValues>) => {
      try {
        await updateWorkingHours({
          brandId: selectedBrandId,
          hoursSettings: values.hoursSettings,
          applyToAll,
        });
      } catch (error) {
        // @ts-ignore
        setErrors(error.fields);
      }
    },
    [selectedBrandId, updateWorkingHours, applyToAll],
  );

  const {
    modalIsOpen,
    openModal,
    handleClose: handleCloseModal,
    handleYes: handleYesModal,
    handleNo: handleNoModal,
  } = useAskModal({
    onYes: useCallback(() => {
      if (formRef.current) {
        setApplyToAll(true);
        // @ts-ignore
        formRef.current.handleSubmit();
      }
    }, [formRef]),
    onNo: useCallback(() => {
      if (formRef.current) {
        setApplyToAll(false);
        // @ts-ignore
        formRef.current.handleSubmit();
      }
    }, [formRef]),
    onClose: useCallback(() => {}, []),
  });

  const selectedBrandName =
    brands.find((brand) => brand.id === selectedBrandId)?.name || '';

  return {
    selectedBrandId,
    selectedBrandName,
    setSelectedBrandId,
    isLoading,
    workingHours,
    showModal,
    setShowModal,
    showBusyModeModal,
    setShowBusyModeModal,
    handleSubmitModalUpdate,
    formRef,
    handleConfirmSubmit: openModal,
    modalIsOpen,
    handleCloseModal,
    handleYesModal,
    handleNoModal,
    busyModeChecked,
    setBusyModeChecked,
    brandsCount: brands.length,
    handleBusyModeOpen,
  };
};

export default useConnect;
